import { cn } from '../../lib/cn'
import { ClubMark } from './ClubMark'
import { Crest } from './Crest'
import { FoundingPill } from './Insignia'
import { romanYear } from '../../lib/roman'
import type { ClubMark as Mark } from '../../lib/types'

interface MemberCardProps {
  /** Member's name as it reads in the register. */
  name: string
  /** Tier label, e.g. "Founding Member". */
  tier: string
  /** Register number, e.g. "No. 007". Omitted until a member actually has one. */
  number?: string
  /** Renders the founding seal pill. */
  founding?: boolean
  /**
   * ISO timestamp the club recorded this standing. Omitted ⇒ the line is left
   * OFF the card. It used to read a hardcoded "Est. Member MMXXVI" on every
   * card on three screens — a claim about this member the club had never been
   * told. The club's own charter year is a different fact and stays in the
   * copy where it belongs.
   */
  since?: string | null
  /** The member's chosen mark, drawn in place of the corner crest. */
  mark?: Mark
  className?: string
}

/**
 * The membership card — the club's object of ceremony. Pine gradient, gilt
 * numerals, foil crest. Held at credit-card proportions (1.586).
 *
 * `.on-pine` so any control laid over it picks up the gilt focus ring.
 */
export function MemberCard({
  name,
  tier,
  number,
  founding = false,
  since,
  mark = 'initial',
  className,
}: MemberCardProps) {
  const year = since ? romanYear(new Date(since).getUTCFullYear()) : null

  return (
    <div
      className={cn(
        'on-pine relative w-full max-w-[380px] overflow-hidden rounded-xl border border-gilt/40 px-7 py-6.5',
        'bg-[linear-gradient(150deg,#1B4130,#153A2A_55%,#0F2C20)] text-paper',
        'shadow-[0_30px_60px_-30px_rgba(15,44,32,0.7)]',
        className,
      )}
      style={{ aspectRatio: '1.586' }}
    >
      {/* Embossed ring, purely decorative. */}
      <span
        aria-hidden="true"
        className="absolute -right-12 -top-12 size-[180px] rounded-full border border-gilt/25"
      />

      <div className="relative z-10 flex h-full flex-col">
        <div className="flex items-start justify-between">
          {/* The member's mark takes the corner when they have chosen one;
              'initial' and 'crest' both keep the club seal, because a crest
              mark and the seal are the same drawing. */}
          {mark === 'initial' || mark === 'crest' ? (
            <Crest tone="gilt" className="size-[52px]" />
          ) : (
            <ClubMark mark={mark} tone="gilt" className="size-[52px]" />
          )}
          <div className="flex flex-col items-end text-right">
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gilt">
              {tier}
            </span>
            {founding && <FoundingPill className="mt-[7px]" />}
          </div>
        </div>

        <p className="mt-auto font-display text-[26px] text-paper">{name}</p>
        <div className="mt-1.5 flex items-baseline justify-between">
          <span className="font-display text-[15px] tracking-[0.08em] tabular-nums text-gilt">
            {number ?? '—'}
          </span>
          {year && (
            <span className="text-[10px] uppercase tracking-[0.18em] text-sage">
              Est. Member {year}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
